/**
 * SeatLockedGate — locks Personal Communication features for users who do
 * not personally own an active Communication Seat.
 *
 * Rules (per Owner spec, Feb 2026):
 *   • When an org buys N seats, those N are for STAFF — the admin does not
 *     get a seat by default. Admin must purchase their own seat separately.
 *   • Personal features (Dialer, Personal SMS, Personal Inbox, Personal Call
 *     Logs, Personal Recordings, Personal Voicemail, Personal Contacts) are
 *     locked until the user has an assigned business number.
 *   • Admins retain access to Org / Analytics / Compliance / Communication
 *     Management screens regardless (that access is granted by role).
 *
 * `user.has_own_seat` is populated by GET /api/auth/me (see routes/auth.py).
 */
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { LockKey, ArrowRight, ShieldStar } from "@phosphor-icons/react";

export default function SeatLockedGate({ feature, children }) {
  const { user } = useAuth();
  const nav = useNavigate();

  // Users bound to an active seat pass through.
  if (user && user.has_own_seat) return children;
  // Owner (platform staff) never sees this gate.
  if (user && user.role === "owner") return children;

  const isAdmin = user?.role === "admin";

  return (
    <div className="min-h-full flex items-center justify-center py-16 px-8" data-testid={`seat-locked-${feature || "feature"}`}>
      <div className="max-w-lg w-full border border-[var(--border)] bg-white p-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 border border-[var(--border)] rounded" aria-hidden>
            <LockKey size={22} weight="fill" className="text-[var(--text-muted)]" />
          </div>
          <div className="font-mono text-[11px] tracking-widest text-[var(--text-muted)] uppercase">
            Locked · {feature || "Personal feature"}
          </div>
        </div>
        <h2 className="font-display font-black text-[28px] leading-[1.05] mb-3" data-testid="seat-locked-title">
          {isAdmin ? "You don't have a Communication Seat yet." : "This feature is locked."}
        </h2>
        <p className="text-[14px] text-[var(--text-secondary)] leading-relaxed mb-6">
          {isAdmin ? (
            <>
              The seats you purchased are for your staff. To use personal calling, SMS, voicemail, recordings and contacts, buy your own Communication Seat. Team analytics, billing, seat management and other admin screens remain available.
            </>
          ) : (
            <>
              Ask your organization admin to assign you an active Communication Seat. Once assigned, this screen will unlock automatically.
            </>
          )}
        </p>

        <div className="border-t border-[var(--border)] pt-5 space-y-2 mb-6">
          {[
            "Outbound & inbound calls",
            "Personal SMS & inbox",
            "Personal call logs",
            "Personal recordings",
            "Personal voicemail",
            "Personal contacts",
          ].map((f) => (
            <div key={f} className="flex items-center gap-2 text-[13px] text-[var(--text-muted)]">
              <LockKey size={12} weight="fill" /> {f}
            </div>
          ))}
        </div>

        {isAdmin ? (
          <button
            className="btn-primary w-full inline-flex items-center justify-center gap-2"
            onClick={() => nav("/app/numbers?admin_seat=1")}
            data-testid="seat-locked-cta-admin"
          >
            <ShieldStar size={16} weight="fill" /> Get a seat for yourself
            <ArrowRight size={14} />
          </button>
        ) : (
          <div className="text-[12px] text-[var(--text-muted)] text-center">
            Please contact your organization admin.
          </div>
        )}
      </div>
    </div>
  );
}
